import React, { useState } from "react";
import { Copy, Link } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import HeaderSection from "../../components/HeaderSection/HeaderSection";
import BottomSection from "../../components/BottomSection/BottomSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@fortawesome/free-solid-svg-icons";

const CampaignUrlBuilder = () => {
    useTheme();
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [utmTerm, setUtmTerm] = useState("");
  const [utmContent, setUtmContent] = useState("");
  const [copied, setCopied] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const buildCampaignUrl = () => {
    if (!websiteUrl.trim() || !utmSource.trim() || !utmMedium.trim() || !utmCampaign.trim()) {
      return "";
    }

    let url = websiteUrl.includes("?") 
      ? `${websiteUrl}&` 
      : `${websiteUrl}?`;

    url += `utm_source=${encodeURIComponent(utmSource)}`;
    url += `&utm_medium=${encodeURIComponent(utmMedium)}`;
    url += `&utm_campaign=${encodeURIComponent(utmCampaign)}`;

    if (utmTerm.trim()) {
      url += `&utm_term=${encodeURIComponent(utmTerm)}`;
    }

    if (utmContent.trim()) {
      url += `&utm_content=${encodeURIComponent(utmContent)}`;
    }

    return url;
  };

  const campaignUrl = buildCampaignUrl();

  const copyToClipboard = async () => {
    if (!campaignUrl) return;
    try {
      await navigator.clipboard.writeText(campaignUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const resetForm = () => {
    setWebsiteUrl("");
    setUtmSource("");
    setUtmMedium("");
    setUtmCampaign("");
    setUtmTerm("");
    setUtmContent("");
  };

  return (
    <>
      <HeaderSection toolId="campaignUrlBuilder" />
      <main className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4 dark:text-white">
        <article className="bg-white dark:bg-gray-800 rounded-xl dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white dark:bg-gray-800 dark:shadow-gray-900/10 dark:border-gray-700">
            <h1 className="text-2xl font-bold mb-2 flex items-center">
              <FontAwesomeIcon icon={faLink} className="mr-3" />
              Campaign URL Builder
            </h1>
            <p className="opacity-90">
              Generate UTM-tagged URLs for tracking campaign performance
            </p>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Form Fields */}
              <div className="space-y-4">
                <div>
                  <label htmlFor="website-url" className="block mb-2 font-medium dark:text-gray-300">
                    Website URL <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Link className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    </div>
                    <input
                      id="website-url"
                      type="url"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="utm-source" className="block mb-2 font-medium dark:text-gray-300">
                    Campaign Source (utm_source) <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="utm-source"
                    type="text"
                    value={utmSource}
                    onChange={(e) => setUtmSource(e.target.value)}
                    className="w-full p-2 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="google, newsletter, social"
                  />
                </div>

                <div>
                  <label htmlFor="utm-medium" className="block mb-2 font-medium dark:text-gray-300">
                    Campaign Medium (utm_medium) <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="utm-medium"
                    type="text"
                    value={utmMedium}
                    onChange={(e) => setUtmMedium(e.target.value)}
                    className="w-full p-2 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="cpc, email, social"
                  />
                </div>
              </div>

              {/* Right Column - Form Fields */}
              <div className="space-y-4">
                <div>
                  <label htmlFor="utm-campaign" className="block mb-2 font-medium dark:text-gray-300">
                    Campaign Name (utm_campaign) <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="utm-campaign"
                    type="text"
                    value={utmCampaign}
                    onChange={(e) => setUtmCampaign(e.target.value)}
                    className="w-full p-2 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="spring_sale, product_launch"
                  />
                </div>

                <div>
                  <label htmlFor="utm-term" className="block mb-2 font-medium dark:text-gray-300">
                    Campaign Term (utm_term) <span className="text-gray-500">(optional)</span>
                  </label>
                  <input
                    id="utm-term"
                    type="text"
                    value={utmTerm}
                    onChange={(e) => setUtmTerm(e.target.value)}
                    className="w-full p-2 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="paid keywords"
                  />
                </div>

                <div>
                  <label htmlFor="utm-content" className="block mb-2 font-medium dark:text-gray-300">
                    Campaign Content (utm_content) <span className="text-gray-500">(optional)</span>
                  </label>
                  <input
                    id="utm-content"
                    type="text"
                    value={utmContent}
                    onChange={(e) => setUtmContent(e.target.value)}
                    className="w-full p-2 rounded-lg border bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="cta_button, banner_ad"
                  />
                </div>
              </div>
            </div>

            {/* Preview Section */}
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex flex-col">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                    Generated Campaign URL
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {!campaignUrl ? "Fill in required fields to generate URL" : "Copy this URL for your campaign"}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={resetForm}
                    className="px-4 py-2 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    Reset
                  </button>
                  <button
                    onClick={copyToClipboard}
                    disabled={!campaignUrl}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-white text-sm font-medium transition-colors shadow ${
                      campaignUrl 
                        ? "bg-green-500 dark:bg-green-600 hover:bg-green-600 dark:hover:bg-green-700" 
                        : "bg-gray-400 dark:bg-gray-600 cursor-not-allowed"
                    }`}
                  >
                    <Copy size={16} />
                    {copied ? "Copied!" : "Copy URL"}
                  </button>
                </div>
              </div>

              <div
                className={`w-full p-4 rounded-lg border bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 overflow-x-auto ${
                  !campaignUrl ? "flex items-center justify-center" : ""
                }`}
              >
                {campaignUrl ? (
                  <a 
                    href={campaignUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-green-600 dark:text-green-400 hover:underline break-all"
                  >
                    {campaignUrl}
                  </a>
                ) : (
                  <span className="text-gray-400 dark:text-gray-500 text-center">
                    Your campaign URL will appear here...
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Help Section */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setShowHelp(!showHelp)}
              className="flex items-center gap-2 text-green-600 dark:text-green-400 hover:underline"
            >
              {showHelp ? "Hide UTM Parameters Help" : "Show UTM Parameters Help"}
            </button>

            {showHelp && (
              <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h3 className="font-semibold mb-2">About UTM Parameters:</h3>
                <ul className="space-y-3 text-sm">
                  <li>
                    <span className="font-medium">utm_source</span> - The referrer (e.g., google, newsletter)
                  </li>
                  <li>
                    <span className="font-medium">utm_medium</span> - Marketing medium (e.g., cpc, email, social)
                  </li>
                  <li>
                    <span className="font-medium">utm_campaign</span> - Product, promo code, or slogan (e.g., spring_sale)
                  </li>
                  <li>
                    <span className="font-medium">utm_term</span> - Paid keywords (optional)
                  </li>
                  <li>
                    <span className="font-medium">utm_content</span> - Differentiate ads (optional)
                  </li>
                </ul>
              </div>
            )}
          </div>
        </article>
      </main>
      <BottomSection toolId="campaignUrlBuilder" />
    </>
  );
};

export default CampaignUrlBuilder;